import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { goToFollowing, goToSearch, pathnames, routes } from 'utils/routerHelpers';
import { isAdmin } from 'utils/userHelpers';

import React from 'react';

import { ActionMenu, Button, Text } from '@thinkific/chalk/react-components';
import {
  BookmarkSimple,
  CaretDown,
  CaretLeft,
  MagnifyingGlass,
} from '@thinkific/chalk/react-icons';

import { useCommunityContext } from 'components/CommunityContext';
import { NotificationPane } from 'components/NotificationPane';
import UserAvatar from 'components/UserAvatar';
import CommunityStatusBadge from 'components/new/CommunityStatusBadge/CommunityStatusBadge';

import * as css from './GlobalHeader.css';

interface GlobalHeaderProps {
  community: {
    name: string;
    id: string;
  };
  user: {
    username: string;
    avatarUrl: string;
    userId: string;
  };
}

export const GlobalHeader = ({ community, user }: GlobalHeaderProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { trackEvent } = useCommunityContext();

  return (
    <header className={css.container} data-qa="top-bar">
      <div className={css.leftContainer}>
        {isAdmin(user) ? (
          <>
            <Text className={css.adminTitle} variant="display.sm">
              {community.name}
            </Text>
            <ActionMenu
              items={[
                {
                  label: t('components-topBar-settings_button', 'Community settings'),
                  onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    trackEvent(TrackingEvent.GO_TO_COMMUNITIES, {
                      [TrackingProperty.COMPONENT]: 'GlobalHeader',
                    });
                    window.open(routes.editCommunity(community.id), '_blank');
                  },
                  href: routes.editCommunity(community.id),
                  target: '_blank',
                },
                {
                  label: t('components-topBar-all-communities_button', 'All communities'),
                  onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    trackEvent(TrackingEvent.GO_TO_CUSTOMIZE_COMMUNITY, {
                      [TrackingProperty.COMPONENT]: 'GlobalHeader',
                    });
                    window.open(routes.communities, '_blank');
                  },
                  href: routes.communities,
                  target: '_blank',
                },
                {
                  label: t('components-topBar-view-dashboard_button', 'View student dashboard'),
                  onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    trackEvent(TrackingEvent.GO_TO_DASHBOARD, {
                      [TrackingProperty.COMPONENT]: 'GlobalHeader',
                    });
                    window.open(routes.dashboard, '_blank');
                  },
                  href: routes.dashboard,
                  target: '_blank',
                },
              ]}
            >
              <Button
                aria-label={
                  t(
                    'components-topBar-admin_dropdown-arialabel',
                    'Open menu for admin settings'
                  ) as string
                }
                emphasis="low"
                icon={CaretDown}
                size="xs"
                hiddenLabel
              >
                {community.name}
              </Button>
            </ActionMenu>
            <CommunityStatusBadge />
          </>
        ) : (
          <>
            <Button
              emphasis="lowCompact"
              href={routes.dashboard}
              icon={CaretLeft}
              size="sm"
              hiddenLabel
              onClick={(e) => {
                e.preventDefault();
                trackEvent(TrackingEvent.GO_TO_DASHBOARD, {
                  [TrackingProperty.COMPONENT]: 'GlobalHeader',
                });
                window.location.href = routes.dashboard;
              }}
            >
              {t('components-topBar-student_dashboard_button', 'My Dashboard')}
            </Button>
            <Text variant="display.sm">{community.name}</Text>
          </>
        )}
      </div>

      <div className={css.rightContainer}>
        <Button
          data-qa="main-search_button"
          emphasis="low"
          href={`/communities/${community.id}/search/`}
          icon={MagnifyingGlass}
          iconStyle={router.pathname === pathnames.search ? 'filled' : 'default'}
          size="sm"
          hiddenLabel
          onClick={(e) => {
            e.preventDefault();
            trackEvent(TrackingEvent.GO_TO_SEARCH, {
              [TrackingProperty.COMPONENT]: 'GlobalHeader',
            });
            goToSearch(router, community.id);
          }}
        >
          {t('pages-search_title', 'Search')}
        </Button>
        <Button
          data-qa="main-following__button"
          emphasis="low"
          href={`/communities/${community.id}/following/`}
          icon={BookmarkSimple}
          iconStyle={router.pathname === pathnames.following ? 'filled' : 'default'}
          size="sm"
          hiddenLabel
          onClick={(e) => {
            e.preventDefault();
            trackEvent(TrackingEvent.GO_TO_FOLLOWING, {
              [TrackingProperty.COMPONENT]: 'GlobalHeader',
            });
            goToFollowing(router, community.id);
          }}
        >
          {t('pages-following_title', 'Following')}
        </Button>
        <NotificationPane />
        <UserAvatar size="xs" {...user} shouldOpenSideMenu />
      </div>
    </header>
  );
};

export default GlobalHeader;
